/**
 * Wire types for the `ifc-mesh-wasm` public mesh surface (v1).
 *
 * Source of truth is the Rust producer, not the generated `pkg/*.d.ts`
 * (which is a gitignored build artifact):
 *   - `crates/ifc-mesh-wasm/src/header.rs`   -> MeshBufferHeader / ChunkHeader
 *   - `crates/ifc-mesh-wasm/src/manifest.rs` -> MeshManifest / ManifestChunk
 *   - `crates/ifc-mesh-wasm/src/georef.rs`   -> Georef
 *   - `crates/ifc-mesh-wasm/src/session.rs`  -> SessionStats
 *
 * Field names below are the serde-serialised names, including the one rename
 * (`IndexRange::type_` is emitted as `index_type`, while the vertex
 * `Attribute::type_` is emitted as `type`).
 */

/** Interleaved vertex stride of the v1 layout, in bytes. */
export const VERTEX_STRIDE_BYTES = 12;

/**
 * Largest quantised position component. The producer clamps to this value, and
 * WebGL/three.js denormalise `UNSIGNED_SHORT` by dividing by exactly this — so
 * `world_mm = quant_origin_mm + normalized * POSITION_QUANT_MAX * quant_scale_mm`.
 */
export const POSITION_QUANT_MAX = 65535;

/** Contract versions this decoder understands. */
export const SUPPORTED_MESH_BUFFER_VERSION = 1;
export const SUPPORTED_MANIFEST_VERSION = 1;

/**
 * Integer-precision note, applying to every `number` below that maps to a Rust
 * `i64`/`u64` (`bbox_mm`, `model_bbox_mm`, `quant_origin_mm`, `storey_level`,
 * `est_vertex_count`): JSON numbers are IEEE-754 doubles, so values beyond
 * 2^53 - 1 are not exactly representable. No real model reaches that in
 * millimetres or in vertex counts, but the types do not enforce it.
 */

/** `[min_x, min_y, min_z, max_x, max_y, max_z]` in world millimetres. */
export type BboxMm = readonly [number, number, number, number, number, number];

export type IndexType = "u16" | "u32";

export interface VertexAttribute {
  readonly name: string;
  readonly type: string;
  readonly count: number;
  readonly offset: number;
}

export interface VertexLayout {
  readonly interleaved: boolean;
  readonly stride_bytes: number;
  readonly attributes: readonly VertexAttribute[];
}

export interface BufferRange {
  readonly byte_offset: number;
  /** Vertex count for `ChunkHeader.vertices`, index count for `indices`. */
  readonly count: number;
}

export interface IndexRange extends BufferRange {
  readonly index_type: IndexType;
}

export interface ChunkHeader {
  readonly chunk_id: number;
  readonly storey_level: number;
  /** Reserved bucketing hint; always `null` in v1 even if `MeshOpts.bucket` was set. */
  readonly bucket: string | null;
  readonly quant_origin_mm: readonly [number, number, number];
  readonly quant_scale_mm: number;
  readonly bbox_mm: BboxMm;
  readonly vertices: BufferRange;
  readonly indices: IndexRange;
  readonly triangle_count: number;
}

export interface MeshBufferHeader {
  readonly mesh_buffer_version: number;
  /** Always `"mm"` in v1. */
  readonly units: string;
  /** Always `"stable"`: identical input bytes produce a byte-identical blob. */
  readonly determinism: string;
  readonly vertex_layout: VertexLayout;
  readonly chunks: readonly ChunkHeader[];
  /**
   * Dense `instanceIndex -> identity` table for the WHOLE model, repeated on
   * every chunk header. Values are canonical UUIDs for valid compressed IFC
   * GlobalIds and the unchanged raw source string otherwise; duplicates and
   * invalid values are preserved rather than replaced.
   */
  readonly guid_table: readonly string[];
}

export interface Georef {
  readonly has_map_conversion: boolean;
  /**
   * Reported as authored in the file (raw `IfcLengthMeasure`, conventionally the
   * project length unit) — NOT converted to millimetres. Do not mm-scale these.
   */
  readonly eastings: number;
  readonly northings: number;
  readonly orthogonal_height: number;
  readonly x_axis_abscissa: number;
  readonly x_axis_ordinate: number;
  readonly scale: number;
  readonly crs_name: string | null;
  readonly crs_vertical: string | null;
  readonly crs_description: string | null;
}

export interface ManifestChunk {
  readonly chunk_id: number;
  readonly storey_level: number;
  readonly storey_name: string | null;
  readonly bbox_mm: BboxMm;
  readonly element_count: number;
  /**
   * Representation-type estimate produced by the plan pass, NOT the meshed
   * vertex count. It is the pre-mesh budget signal and may over- or
   * under-shoot the real count of the built chunk.
   */
  readonly est_vertex_count: number;
}

export interface MeshManifest {
  readonly manifest_version: number;
  readonly units: string;
  readonly determinism: string;
  readonly model_bbox_mm: BboxMm;
  readonly georef: Georef;
  /** Geometry products whose representations produced no mesh (3D No-Silent-Loss ledger). */
  readonly unmeshed_geometry_products: number;
  /** The same tally by IFC class. */
  readonly unmeshed_geometry_by_class: Readonly<Record<string, number>>;
  /** Geometry products stating only curve geometry (no body to mesh). */
  readonly axis_only_geometry_products: number;
  /** The axis-only tally by IFC class. */
  readonly axis_only_by_class: Readonly<Record<string, number>>;
  /** Geometry products stating no representation reference at all. */
  readonly geometry_products_without_representation: number;
  readonly chunks: readonly ManifestChunk[];
}

/** Capacity-based live ownership telemetry; distinct from RSS and WASM pages. */
export interface SessionStats {
  readonly live_session_count: number;
  readonly live_model_count: number;
  readonly live_projection_count: number;
  readonly retained_product_mesh_count: number;
  readonly model_logical_bytes: number;
  readonly plan_logical_bytes: number;
  readonly retained_mesh_bytes: number;
  readonly retained_logical_bytes: number;
  /** Hard-coded 0 in v1: `mesh_chunk` re-meshes into a temporary every call. */
  readonly mesh_cache_budget_bytes: number;
  readonly chunk_requests: number;
  readonly peak_chunk_working_bytes: number;
  readonly peak_projection_working_bytes: number;
}

/**
 * A failure raised by the engine.
 *
 * The WASM exports are synchronous `Result<_, JsValue>`: a failure is a *thrown*
 * JSON string of the shape `{"error": "..."}`. The doc comment on
 * `mesh_buffer_from_ifc` in `lib.rs` still says "the promise rejects", which
 * describes a shape the function never had — do not build retry logic on it.
 */
export class IfcEngineError extends Error {
  /** Engine entry point that threw, e.g. `"open_ifc"`. */
  readonly op: string;
  /** The thrown value verbatim, so the UI can show the engine's own words. */
  readonly raw: string;

  constructor(op: string, message: string, raw: string) {
    super(`${op}: ${message}`);
    this.name = "IfcEngineError";
    this.op = op;
    this.raw = raw;
  }
}

/** Normalise a thrown JS value into a typed engine failure. */
export function toEngineError(op: string, thrown: unknown): IfcEngineError {
  if (thrown instanceof IfcEngineError) {
    return thrown;
  }
  const raw =
    typeof thrown === "string"
      ? thrown
      : thrown instanceof Error
        ? thrown.message
        : String(thrown);

  let message = raw;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) {
      const inner: unknown = (parsed as Record<string, unknown>)["error"];
      if (typeof inner === "string") {
        message = inner;
      }
    }
  } catch {
    // Not JSON (a panic string, a TypeError from the glue, ...): keep it verbatim.
  }
  return new IfcEngineError(op, message, raw);
}

/**
 * Renderer input. v1 only ever produces `meshChunk`; `faceBatch` is the A'
 * (face-identity) seam and is reserved so the renderer signature does not have
 * to change when it lands. Nothing in this repo constructs it today.
 */
export type Renderable =
  | { readonly kind: "meshChunk"; readonly header: ChunkHeader; readonly blob: Uint8Array }
  | { readonly kind: "faceBatch"; readonly faces: readonly FaceRecord[] };

/**
 * Future A' face record. The viewer never canonicalises, orients or derives
 * identity — that is the engine's contract claim. `identity` is an opaque key:
 * a face operation yields a *different* face, not "the same face moved".
 */
export interface FaceRecord {
  readonly identity: string;
  /** Normalised integer plane `(a, b, c, d)`. */
  readonly planeMm: readonly [number, number, number, number];
  /** Outer ring first, inner rings after. */
  readonly ringsMm: readonly Int32Array[];
}

/**
 * Additive session API `mesh_session_element_info_candidate`: one entry per
 * `instanceIndex`, aligned with the `guid_table`. v1 carries the three shallow
 * display attributes the mesh surface deliberately excludes from the per-chunk
 * header — it is a separate, call-once, idempotent table.
 *
 * Source: `crates/ifc-mesh-wasm/src/session.rs` `element_info_candidate_json`.
 */
export interface ElementInfoEntry {
  readonly name: string | null;
  readonly ifc_class: string | null;
  readonly description: string | null;
}

export interface ElementInfoTable {
  readonly schema: string;
  readonly mesh_buffer_version: number;
  readonly attributes: readonly string[];
  readonly entry_count: number;
  readonly null_counts: {
    readonly name: number;
    readonly ifc_class: number;
    readonly description: number;
  };
  readonly element_info: readonly ElementInfoEntry[];

}

/** One axis-only product: curve geometry only, no body in the source. */
export interface AxisOnlyEntry {
  readonly express_id: number;
  readonly ifc_class: string;
}

/** The axis-only candidate table (mesh-axis-only-candidate export). */
export interface AxisOnlyTable {
  readonly schema: string;
  readonly mesh_buffer_version: number;
  readonly entry_count: number;
  readonly entries: readonly AxisOnlyEntry[];
}

/** Session/source-record-scoped identity for one drawable or refused Axis row. */
export interface AxisLineIdentity {
  readonly scope: "live_session_source_record";
  readonly express_id: number;
}

/** Source-authored open Axis polylines for one axis-only product. */
export interface AxisLineEntry {
  readonly identity: AxisLineIdentity;
  readonly ifc_class: string;
  /** One-origin storey level, aligned with the existing mesh selection display. */
  readonly storey_level: number;
  readonly topology: "open_polyline";
  readonly polylines_mm: readonly (readonly (readonly [number, number])[])[];
  /** Length of the displayed source Axis, never the product's physical length. */
  readonly axis_length_mm: number;
}

export interface AxisLineRefusal {
  readonly identity: AxisLineIdentity;
  readonly ifc_class: string;
  readonly reason: string;
}

/** Default-off staged-D B producer envelope. */
export interface AxisLinesTable {
  readonly schema: "ifc-mesh-session-axis-lines/candidate-1";
  readonly mesh_buffer_version: 1;
  readonly units: "mm";
  readonly identity_scope: "live_session_source_record";
  readonly source_entry_count: number;
  readonly drawable_entry_count: number;
  readonly refused_entry_count: number;
  readonly entries: readonly AxisLineEntry[];
  readonly refusals: readonly AxisLineRefusal[];
}

/** v1 source plan projection options. Sent to `source_plan_projection_manifest`. */
export interface PlanProjectionOpts {
  readonly version: 1;
  readonly cut_height_mm: number;
  readonly cut_band_mm: number;
  readonly view_below_mm: number;
  readonly view_above_mm: number;
  readonly tolerance_mm: number;
  readonly hidden_lines: "none";
  readonly include_mep: boolean;
  readonly mep_representation: "centerline";
}

export interface PlanProjectionStoreyInfo {
  readonly expressId: string;
  readonly guid: string | null;
  readonly name: string | null;
  readonly elevationMm: number;
}

export interface PlanProjectionManifestChunk {
  readonly chunkId: number;
  readonly storey: PlanProjectionStoreyInfo;
  readonly bboxMm: readonly [number, number, number, number];
}

export interface PlanProjectionManifest {
  readonly projectionVersion: number;
  readonly projectionId: number;
  readonly units: string;
  readonly determinism: string;
  readonly options: PlanProjectionOpts;
  readonly chunks: readonly PlanProjectionManifestChunk[];
}

export interface PlanProjectionSourceRef {
  readonly expressId: string;
  readonly guid: string | null;
  readonly ifcClass: string;
}

export type PlanPrimitiveKind = "cut" | "projected" | "centerline" | "grid";
export type PlanPrimitiveTopology = "closed_polyline" | "open_polyline";
export type PlanPrimitiveLayer = "architecture" | "mep" | "grid";

export interface PlanProjectionPrimitive {
  readonly primitiveId: number;
  readonly source: PlanProjectionSourceRef;
  readonly layer: PlanPrimitiveLayer;
  readonly kind: PlanPrimitiveKind;
  readonly topology: PlanPrimitiveTopology;
  readonly byteOffset: number;
  readonly pointCount: number;
  readonly provenance: string;
}

export interface PlanProjectionAccounting {
  readonly sourceCandidates: number;
  readonly emittedSources: number;
  readonly omittedSources: number;
  readonly excludedOutsideRange: number;
}

export interface PlanProjectionOmission {
  readonly expressId: string;
  readonly guid: string | null;
  readonly ifcClass: string;
  readonly reason: string;
}

export interface PlanProjectionChunkHeader {
  readonly projectionVersion: number;
  readonly projectionId: number;
  readonly chunkId: number;
  readonly storey: PlanProjectionStoreyInfo;
  readonly originMm: readonly [number, number];
  readonly bboxMm: readonly [number, number, number, number];
  readonly coordinateEncoding: "i32le_xy_relative_to_origin";
  readonly primitives: readonly PlanProjectionPrimitive[];
  readonly accounting: PlanProjectionAccounting;
  readonly omissions: readonly PlanProjectionOmission[];
}

export interface PlanProjectionChunk {
  readonly header: PlanProjectionChunkHeader;
  readonly blob: Uint8Array;
}
